import {createContext} from '@designeo/js-helpers';
import * as Sentry from '@sentry/vue';
import {registerCoreModule} from '@/modules/Core';
import debugTools from '@/plugins/debugTools';
import imageProxy from '@/plugins/imageProxy';
import {registerPinia} from '@/plugins/Pinia/pinia';
import App from './App.vue';
import {registerNotificationModule} from './modules/Notification';
import {registerModuleSystem} from './moduleSystem';
import {registerVueI18n} from './plugins/I18n/i18n';
import {registerRouter} from './plugins/Router/router';

interface createContextOptions {
  createApp: import('vue').CreateAppFunction<any>,
}

export const createCtx = async ({createApp}: createContextOptions) => {
  const app = createApp(App)
    .use(debugTools)
    .use(imageProxy, {
      baseUrl: window.origin,
    });

  if (APP_CONFIG.isBuild) {
    // we're now counting on sentry
    // const {tap} = useConsoleLogger();
    // tap();
    try {
      Sentry.init({
        app,
        dsn: APP_CONFIG.sentryDsn,
        tracesSampleRate: 1.0,
        environment: APP_CONFIG.environment,
        release: APP_CONFIG.version,
      });
    } catch (e) {
      console.error(e);
      console.error('Sentry is disabled.');
    }
  }

  const ctx = createContext({
    app,
  })
    .register(registerModuleSystem)
    .register(registerPinia)
    .register(registerCoreModule)
    .register(registerNotificationModule)
    .register(registerVueI18n)
    .register(registerRouter)
  ;
  const resolvedContext = await ctx.resolve();
  app.config.globalProperties.$context = resolvedContext;

  return resolvedContext;
};

type ThenArg<T> = T extends PromiseLike<infer U> ? U : T
export type AppContext = ThenArg<ReturnType<typeof createCtx>>

export default createCtx;
